import { useCallback, useEffect, useState } from 'react';
import { readStored, writeStored } from '@/lib/native';

/**
 * A locally-persisted list — the app's local-first data primitive.
 *
 * Every screen reads from here, so the app renders real content on launch with no network. Use this
 * as the source of truth and sync to a backend in the background if the app has one; never make the
 * first paint depend on a request.
 */
export function useLocalCollection<T extends { id: string }>(key: string, seed: T[] = []) {
	const [items, setItems] = useState<T[]>(seed);
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		let cancelled = false;
		readStored<T[]>(key).then((stored) => {
			if (cancelled) return;
			if (stored) setItems(stored);
			setLoaded(true);
		});
		return () => {
			cancelled = true;
		};
	}, [key]);

	// Only persist after the initial read, otherwise the empty default overwrites saved data on mount.
	useEffect(() => {
		if (!loaded) return;
		void writeStored(key, items);
	}, [key, items, loaded]);

	const add = useCallback((item: T) => setItems((prev) => [item, ...prev]), []);
	const remove = useCallback((id: string) => setItems((prev) => prev.filter((i) => i.id !== id)), []);
	const update = useCallback(
		(id: string, patch: Partial<T>) =>
			setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i))),
		[],
	);

	return { items, loaded, add, remove, update, setItems };
}
